import type { BlogArticle } from '@/lib/blog-data'
import { PUBLIC_SOUND_COUNT_LABEL } from '@/lib/public-sound-count'

export const post: BlogArticle = {
  slug: 'dukes-of-hazzard-horn-sound-tesla',
  // TODO(content): custom header pending — infsh image-gen credits were at $0 at publish time.
  title: 'Dukes of Hazzard Horn Sound: General Lee\'s Dixie Horn as a Tesla Lock Chime',
  excerpt:
    'Download the General Lee\'s Dixie horn from Dukes of Hazzard as a free Tesla lock chime. The eight-note fanfare from 1979, formatted as LockChime.wav.',
  content: `
# Dukes of Hazzard Horn Sound: The General Lee's Dixie Horn on Tesla

The General Lee's horn is one of the most recognizable car sounds on television: an eight-note blast of "Dixie" that announced Bo and Luke Duke were about to jump another creek in their bright-orange 1969 Dodge Charger. Dukes of Hazzard ran on CBS from 1979 to 1985, and that horn outlived the show by decades, showing up in car shows, toy replicas, and now, custom Tesla lock chimes.

One version of the horn is in the library, free, formatted as \`LockChime.wav\`, ready to install.

## What the Dixie Horn Actually Is

The horn plays the first few bars of "Dixie" (the Civil War-era tune associated with the American South) through an aftermarket horn kit installed on the General Lee for the show. It's not a stock Dodge Charger horn; it was added specifically as a character beat, the same way KITT's scanner or a Star Trek communicator chirp is a piece of sound design built to make a vehicle feel like a character. The horn became so tied to the car that it's often called the "General Lee horn" as often as the "Dixie horn," even though it's the same eight-note clip.

### General Lee Horn (Dukes of Hazzard Dixie)

The full horn blast, eight notes, 2.7 seconds. This is the classic clip: instantly recognizable to anyone who watched the show or has seen a General Lee replica at a car meet.

[Download General Lee Horn →](/sounds/general-lee-dixie-horn-dukes-of-hazzard-classic)

## Why a 1979 Car Horn Works as a Tesla Lock Chime

A horn clip is built to do one job: cut through outdoor noise and get noticed at a distance. That's the same requirement Tesla's external speaker has for a lock chime, so a recording engineered for a car horn translates cleanly instead of turning to mush through a small speaker. The Dixie horn in particular has no dialogue or crowd noise layered in, just the fanfare itself, which keeps it short and unambiguous: you hear it, you know your Tesla is locked, and (if you grew up on Saturday reruns) you probably grin a little.

It's also a genuinely different lane from most novelty lock chimes. Most of what people install is either a meme sound effect or a sci-fi bleep; the Dixie horn is closer to swapping in a classic car's signature the way some owners chase a specific engine note. If you want your Tesla to nod at a different era of car culture instead of a different universe, this is the sound for it.

## How to Install the Dukes of Hazzard Horn Sound

The download above is already formatted as \`LockChime.wav\`, Tesla's required filename. No conversion step needed.

1. **Download** the General Lee horn (one click, no account)
2. **Create the folder**: place \`LockChime.wav\` inside a \`Boombox\` folder at the root of a FAT32 or exFAT USB drive, so the full path reads \`Boombox/LockChime.wav\`
3. **Plug in** the drive to a USB port that carries data
4. **Select it**: go to **Toybox → Boombox → Lock Sound**, choose USB, and pick your file
5. **Lock the car** and let the Dixie horn play

Full walkthrough with screenshots: [Tesla Lock Sound Installation Guide →](/guide)

## More Retro and Vehicle Sounds

The General Lee horn lives in the [Movies & TV category](/sounds/category/movies) alongside other iconic small-screen audio. If classic-car and vehicle sounds are more your interest, the [Vehicles & Transport category](/sounds/category/vehicles-transport) has more horns, engines, and road-trip audio to browse.

Want another retro TV car? See our [Tesla KITT scanner lock sound](/blog/tesla-kitt-scanner-lock-sound) roundup for Knight Rider's AI sweep.

## Frequently Asked Questions

**What show is the Dukes of Hazzard horn sound from?**
Dukes of Hazzard, the CBS series that ran from 1979 to 1985. The horn plays on the General Lee, the Duke boys' bright-orange 1969 Dodge Charger, and became one of the show's signature effects alongside its jumps and chase scenes.

**Is the Dukes of Hazzard horn sound free to download?**
Yes. The version on this page is a free \`LockChime.wav\` download, no account required.

**What's the difference between the "Dukes of Hazzard horn" and the "Dixie horn"?**
They're the same recording. "General Lee horn," "Dixie horn," "Dukes of Hazzard car horn," and the common misspelling "Dukes of Hazard" (one Z) all point people to the same eight-note clip.

**Will this work on Cybertruck?**
Yes. Every sound on this site downloads as a properly formatted \`LockChime.wav\` file that works across Model 3, Model Y, Model S, Model X, and Cybertruck.

**Do I need to convert the file first?**
No. The download is already a WAV file named \`LockChime.wav\`, formatted to Tesla-compatible spec. Drop it straight into the \`Boombox\` folder on your USB drive.

---

Browse the full [sound library →](/sounds) for ${PUBLIC_SOUND_COUNT_LABEL} more free lock sounds, or check the [installation guide](/guide) if this is your first time setting up a custom lock chime.
    `,
  author: 'Tesla Lock Sound Team',
  publishedAt: '2026-09-22',
  category: 'sound-spotlights',
  tags: ['dukes of hazzard', 'general lee', 'dixie horn', 'tv shows', 'retro', 'lock sound', 'model 3', 'model y'],
  readingTime: 4,
  featured: false,
  soundReferences: [
    { title: 'General Lee Horn (Dukes of Hazzard Dixie)', id: 'd8201688-f223-4f6c-acfa-2fdbd2259eab' },
  ],
  inlineSounds: {
    'General Lee Horn (Dukes of Hazzard Dixie)': 'd8201688-f223-4f6c-acfa-2fdbd2259eab',
  },
}
